import { useRef } from 'react';
import { RiCheckFill } from 'react-icons/ri';

import { useFormik } from 'formik';
import { mutate } from 'swr';
import * as Yup from 'yup';

import { FontSize, FontWeight, refuseReasonsOptions } from 'commons';
import { Button, Heading, Product, Select, Text } from 'components';
import { formValidations, messages, swrKeys } from 'helpers';
import { useAuth } from 'hooks';
import { consignmentService } from 'services';
import { Entry } from 'types';

import * as S from './styles';

const DESCRIPTION_CHARACTER_LIMIT = 250;

interface FormProps {
  selectedProducts: string[];
  reason: string;
  description: string;
}

interface Props {
  setSelectedConsignment: (consignment: null) => void;
  consignmentCode: string;
  store: string;
  entries: Entry[];
  setShowRefuse: (value: boolean) => void;
}

export function Refuse({
  setSelectedConsignment,
  consignmentCode,
  store,
  entries,
  setShowRefuse,
}: Props) {
  const { user } = useAuth();
  const { isAdmin } = user;

  const revokeValidation = Yup.object().shape({
    reason: Yup.string().required('Motivo'),
  });

  const formRefuse = useFormik({
    initialValues: {
      selectedProducts: entries.map(entry => entry.product.legacySKU),
      reason: '',
      description: '',
    },

    validateOnChange: false,
    validationSchema: revokeValidation,
    validate: values => {
      formValidations.validationSchema({ schema: revokeValidation, values });
    },

    onSubmit: async (values: FormProps) => {
      const { reason, description, selectedProducts } = values;

      if (!selectedProducts.length) {
        return messages([
          { errorCode: 'FRONT-RE-PRODUCT_NOT_SELECTED_TO_REFUSE_CONSIGNMENT' },
        ]);
      }

      const response = await consignmentService.putRefuse({
        consignmentCode,
        store,
        reason: reason,
        description: description ? description : undefined,
        selectedProducts,
      });

      if (response?.status === 200) {
        messages([
          { errorCode: 'FRONT-RE-SERVICE_REFUSED' },
          { subject: consignmentCode, subjectType: 'consignmentCode' },
        ]);

        mutate(swrKeys.consignments({ key: store }));
        setSelectedConsignment(null);
      }
    },
  });

  const { values, errors, handleChange, isSubmitting } = formRefuse;

  const itemsRef = useRef<any>([]);

  const renderProducts = entries.map((entry, index: number) => {
    const { legacySKU } = entry.product;

    function handleProductSelect() {
      const { current } = itemsRef;

      if (current) {
        current[index].click();
      }
    }

    const isSelected = values.selectedProducts.includes(legacySKU);

    return (
      <S.ProductWrapper
        $selected={isSelected}
        onClick={handleProductSelect}
        key={legacySKU}
      >
        <S.CheckboxWrapper>
          <S.Checkbox>{isSelected && <RiCheckFill />}</S.Checkbox>

          <input
            hidden
            type="checkbox"
            name="selectedProducts"
            id="selectedProducts"
            value={legacySKU}
            ref={el => (itemsRef.current[index] = el)}
            checked={isSelected}
            onChange={handleChange}
            disabled={isAdmin}
          />
        </S.CheckboxWrapper>

        <Product
          entry={entry}
          hideCopyButton
          status={isSelected ? 'error' : 'default'}
        />
      </S.ProductWrapper>
    );
  });

  const charactersAvailable =
    DESCRIPTION_CHARACTER_LIMIT - values.description.length;

  function getLimiterMessage(): string {
    if (charactersAvailable === 1) {
      return '1 caracter';
    }

    return `${charactersAvailable} caracteres`;
  }

  function handleClose() {
    setShowRefuse(false);
  }

  const characterLimiterMessage = getLimiterMessage();

  const textAreaPlaceholder =
    values.reason === 'STOCK_DIVERGENCE'
      ? 'Insira aqui o número do chamado aberto junto ao Suporte Easy ou o Código do Serviço.'
      : 'Ex.: Pedido contém cupom de desconto não praticado pela loja';

  return (
    <S.Form onSubmit={formRefuse.handleSubmit}>
      <S.TitleWrapper>
        <Heading size={FontSize.font16} weight={FontWeight.bold}>
          Cancelamento do pedido
        </Heading>

        <Text size={FontSize.font12}>
          Você só pode realizar o cancelamento do pedido completo, selecione no
          campo abaixo o motivo e descreva caso existam informações
          complementares.
        </Text>
      </S.TitleWrapper>

      <S.SelectWrapper>
        <Text size={FontSize.font12}>
          Informe o motivo do cancelamento da venda:
        </Text>

        <Select.Wrapper
          name="reason"
          onChange={handleChange}
          options={refuseReasonsOptions}
          status={errors.reason ? 'error' : undefined}
          disabled={isAdmin}
        >
          <Select.Input placeholder="Selecione..." />

          <Select.StatusArrow />
        </Select.Wrapper>
      </S.SelectWrapper>

      <S.TextareaWrapper>
        <Text size={FontSize.font12}>
          Descreva o motivo <span className="opaque">(opcional)</span>
        </Text>

        <S.Textarea
          name="description"
          id="description"
          placeholder={textAreaPlaceholder}
          cols={3}
          rows={5}
          maxLength={DESCRIPTION_CHARACTER_LIMIT}
          value={values.description}
          onChange={handleChange}
          disabled={isAdmin}
        />

        <S.CharacterCounterWrapper>
          <Text size={FontSize.font12}>{characterLimiterMessage}</Text>
        </S.CharacterCounterWrapper>
      </S.TextareaWrapper>

      <S.ButtonsWrapper>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={isAdmin}
        >
          Continuar com a venda
        </Button>

        <Button
          variant="danger"
          type="submit"
          disabled={isSubmitting || isAdmin}
        >
          Cancelar atendimento
        </Button>
      </S.ButtonsWrapper>

      <Text>Itens do pedido</Text>

      <S.ProductListWrapper>{renderProducts}</S.ProductListWrapper>
    </S.Form>
  );
}
